import { CampoAlteracao, DadosPendenciaAceita } from "./types";
import { mapearAnexos } from "../../Compartilhado/utils";
import type { Email } from "../DemandaCriada/types";
import { formatFileSize } from "../../../../../../../utility/byteSizeUtils";
import { formatCurrency } from "../../../../../../../utility/currencyUtils";
import { parseJsonSafely } from "../../../../../../../utility/jsonUtils";
import { extractFilename } from "../../../../../../../utility/filePathUtils";
import { mapearEmail } from "../../../../../../../utility/historicoLogUtils";
import { getStatusLabelStatusKanban, getResultadoLabelPendencia } from "../../../../../../../utility/statusPendenciaKanban";

export const mapearCamposUnificados = (logPendencia: any, dadosExtra: any): CampoAlteracao[] => {
    // Seguindo o padrão do PendenciaMovida: verificar se dados_depois existe (dados_antes pode ser null)
    if (!logPendencia || !logPendencia.dados_depois) {
        return [];
    }

    const dadosAntes = parseJsonSafely(logPendencia.dados_antes);
    const dadosDepois = parseJsonSafely(logPendencia.dados_depois);
    const extra = parseJsonSafely(dadosExtra);
    
    const campos: CampoAlteracao[] = [];

    // Resultado - sempre exibir quando é 'aceita' ou quando há mudança
    const resultadoAntes = dadosAntes?.resultado ?? null;
    const resultadoDepois = dadosDepois?.resultado ?? null;
    
    // Sempre exibir se resultado é 'aceita' ou se houve mudança
    if (resultadoDepois === 'aceita' || resultadoAntes !== resultadoDepois) {
        campos.push({
            campo: 'Resultado',
            antes: getResultadoLabelPendencia(resultadoAntes),
            depois: getResultadoLabelPendencia(resultadoDepois)
        });
    }

    // Status - exibir se houver mudança
    if (dadosAntes?.status_kanban && dadosDepois?.status_kanban) {
        if (dadosAntes.status_kanban !== dadosDepois.status_kanban) {
            campos.push({
                campo: 'Status',
                antes: getStatusLabelStatusKanban(dadosAntes.status_kanban),
                depois: getStatusLabelStatusKanban(dadosDepois.status_kanban)
            });
        }
    }

    // Campo 'aceita' para pendências financeiras
    const aceitaAntes = dadosAntes?.aceita ?? null;
    const aceitaDepois = dadosDepois?.aceita ?? null;
    if (aceitaAntes !== aceitaDepois) {
        campos.push({
            campo: 'Aceita',
            antes: aceitaAntes ? (aceitaAntes === 'sim' ? 'Sim' : 'Não') : '—',
            depois: aceitaDepois ? (aceitaDepois === 'sim' ? 'Sim' : 'Não') : '—'
        });
    }
    
    // Campo 'resolvida' que também muda quando aceita
    const resolvidaAntes = dadosAntes?.resolvida ?? null;
    const resolvidaDepois = dadosDepois?.resolvida ?? null;
    if (resolvidaAntes !== resolvidaDepois) {
        campos.push({
            campo: 'Resolvida',
            antes: resolvidaAntes ? (resolvidaAntes === 'sim' ? 'Sim' : 'Não') : '—',
            depois: resolvidaDepois ? (resolvidaDepois === 'sim' ? 'Sim' : 'Não') : '—'
        });
    }

    return campos;
};

export const processarDadosPendenciaAceita = (logs: any[]): DadosPendenciaAceita => {
    const logPendencia = logs.find((log: any) => log.entidade_nome === 'pendencia');
    
    if (!logPendencia) {
        return {
            campos: [],
            anexos: [],
            email: null,
            idPendencia: undefined
        };
    }

    const dadosExtra = parseJsonSafely(logPendencia.dados_extra);
    const campos = mapearCamposUnificados(logPendencia, dadosExtra);
    let anexos = mapearAnexos(logs);
    
    if (dadosExtra && dadosExtra.arquivos && Array.isArray(dadosExtra.arquivos)) {
        const anexosIdsMapeados = new Set(anexos.map(a => a.id).filter(Boolean));
        const logsArquivosMap = new Map();
        
        for (const log of logs) {
            if ((log.entidade_nome === 'proposta_arquivo' || log.entidade_nome === 'orientacao_arquivo') && log.entidade_id) {
                const logDadosExtra = parseJsonSafely(log.dados_extra);
                logsArquivosMap.set(log.entidade_id, logDadosExtra);
            }
        }
        
        for (const arquivo of dadosExtra.arquivos) {
            if (arquivo.id_proposta_arquivo && !anexosIdsMapeados.has(arquivo.id_proposta_arquivo)) {
                const caminho = arquivo.caminho;
                const filename = extractFilename(caminho);
                const logArquivo = logsArquivosMap.get(arquivo.id_proposta_arquivo);
                const tamanhoArquivo = arquivo.tamanho || logArquivo?.tamanho;
                const size = formatFileSize(tamanhoArquivo);
                const excluidoAtual = arquivo.excluido === true || arquivo.excluido === 1;
                
                if (caminho && filename && filename !== '—') {
                    anexos.push({
                        id: arquivo.id_proposta_arquivo,
                        filename,
                        size: size !== '—' ? size : '—',
                        excluido: excluidoAtual
                    });
                }
            }
        }
    }
    
    const email = mapearEmail(logs);

    return {
        campos,
        anexos,
        email,
        idPendencia: logPendencia.entidade_id
    };
};
